import { Box, Chip, Tooltip, useTheme } from "@mui/material";
import { spacing, statusChipStyle } from "@aeriosiq/ui";
import { CircleCheckIcon, LockIcon } from "@aeriosiq/ui/icons";

/**
 * TT-02 — the lock state of one tracking. `🔒 invoiced` vs `editable` in the
 * mockups.
 *
 * `attention` for locked, not `critical`: a locked tracking is not a failure,
 * it is a row that now needs a human (a platform admin) if it has to change.
 * `success` for editable — the row is in the state the pilot can still act on.
 *
 * The tooltip is the whole explanation the story asks for ("tapping explains
 * the lock"), so the reason travels with the chip instead of living in a
 * separate dialog every surface has to re-implement.
 */
export const LOCKED_EXPLANATION =
  "This time tracking was locked when the invoice was raised, so its text and " +
  "times can no longer change — printed and stored data must never diverge. " +
  "Only a platform administrator can unlock it.";

export interface LockStateChipProps {
  isLocked: boolean;
  dense?: boolean;
}

export function LockStateChip({ isLocked, dense = false }: LockStateChipProps) {
  const theme = useTheme();
  const style = statusChipStyle(theme, isLocked ? "attention" : "success");
  const label = isLocked ? "Invoiced" : "Editable";
  const Glyph = isLocked ? LockIcon : CircleCheckIcon;

  const chip = (
    <Chip
      label={label}
      size={dense ? "small" : "medium"}
      variant="filled"
      icon={
        <Box
          component="span"
          aria-hidden
          sx={{ display: "inline-flex", color: style.dotColor }}
        >
          <Glyph fontSize="small" />
        </Box>
      }
      sx={{
        backgroundColor: style.backgroundColor,
        color: style.color,
        flexShrink: 0,
        fontWeight: 500,
        "& .MuiChip-label": { color: style.color },
        "& .MuiChip-icon": {
          marginInlineStart: spacing.sm,
          marginInlineEnd: 0,
        },
      }}
      data-lock-state={isLocked ? "locked" : "editable"}
      aria-label={isLocked ? `Locked. ${LOCKED_EXPLANATION}` : "Editable"}
    />
  );

  if (!isLocked) return chip;
  return (
    <Tooltip
      title={LOCKED_EXPLANATION}
      enterTouchDelay={0}
      leaveTouchDelay={6000}
    >
      {chip}
    </Tooltip>
  );
}
